import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Search, Building2, DollarSign, Download, Eye, CheckCircle2, AlertCircle, Ban, RotateCcw, X, Trash2 } from 'lucide-react';
import { generateSalesOrderPDF } from '../utils/pdfGenerator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CUENTAS_POR_IMPORTADORA = {
  GLOBALGRAIN: { clp: '0-000-8431148-0', usd: '0-051-0074286-3' },
  AGROTRANS:   { clp: '0-000-7031512-2', usd: '0-051-0024106-6' },
};

const CALIDAD_POR_PRODUCTO = {
  MAIZ: '14,5% H° MAX',
  TRIGO: '14% H° MAX',
};

const INITIAL_FORM = {
  business_type: '',  // 'AGROTRANS' o 'GLOBALGRAIN'
  importadora_id: '',
  client_id: '',
  contacto: '',
  email_contacto: '',
  direccion_comercial: '',
  producto: '',
  producto_otro: '',
  cantidad_toneladas: '',
  cantidad_camiones: '',
  precio_unitario: '',
  calidad: '',
  cronograma_items: [{ camiones: '', fecha: '' }],  // Array estructurado de cronograma
  direccion_entrega: '',
  dias_pago: '',  // Cantidad de días para condición de pago
  cuenta_corriente: '',
  banco: 'SANTANDER',
  emails_comprobantes: '',
  fecha: new Date().toISOString().split('T')[0],
  cierre: ''
};

const Businesses = () => {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [importadoras, setImportadoras] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [justification, setJustification] = useState('');
  const [filterText, setFilterText] = useState('');
  const [viewBusiness, setViewBusiness] = useState(null);
  // Filtros individuales
  const [filterEstado, setFilterEstado] = useState('all');
  const [filterProducto, setFilterProducto] = useState('all');
  const [filterFecha, setFilterFecha] = useState('');

  const completarNegocio = async (biz) => {
    if (!window.confirm(`¿Completar el negocio ${biz.business_number}? Esta acción cierra el negocio administrativamente.`)) return;
    try {
      await axios.put(`${API}/businesses/${biz.id}/completar`);
      toast.success(`Negocio ${biz.business_number} marcado como Completado`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al completar negocio');
    }
  };

  const anularNegocio = async (biz) => {
    if (!window.confirm(`¿Anular el negocio ${biz.business_number}? Solo es posible si tiene 0 toneladas entregadas.`)) return;
    try {
      await axios.put(`${API}/businesses/${biz.id}/anular`);
      toast.success(`Negocio ${biz.business_number} anulado`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al anular negocio');
    }
  };

  const restaurarNegocio = async (biz) => {
    if (!window.confirm(`¿Restaurar el negocio ${biz.business_number} a estado "En Proceso"?`)) return;
    try {
      await axios.put(`${API}/businesses/${biz.id}/restaurar`);
      toast.success(`Negocio ${biz.business_number} restaurado`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al restaurar negocio');
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [bizRes, impRes, clientRes] = await Promise.all([
        axios.get(`${API}/businesses`),
        axios.get(`${API}/importadoras`),
        axios.get(`${API}/clients`)
      ]);
      setBusinesses(bizRes.data);
      setImportadoras(impRes.data);
      setClients(clientRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const filteredBusinesses = useMemo(() => {
    let result = [...businesses];
    // Filtro de texto libre
    if (filterText) {
      const term = filterText.toLowerCase();
      result = result.filter(b => {
        const client = clients.find(c => c.id === b.client_id);
        const imp = importadoras.find(i => i.id === b.importadora_id);
        const fechaFormatted = b.fecha ? b.fecha.split('-').reverse().join('/') : '';
        return (
          b.business_number?.toLowerCase().includes(term) ||
          b.producto?.toLowerCase().includes(term) ||
          b.cierre?.toLowerCase().includes(term) ||
          b.fecha?.toLowerCase().includes(term) ||
          fechaFormatted.includes(term) ||
          b.client_name?.toLowerCase().includes(term) ||
          client?.razon_social?.toLowerCase().includes(term) ||
          imp?.alias?.toLowerCase().includes(term)
        );
      });
    }
    // Filtros individuales
    if (filterEstado !== 'all') result = result.filter(b => b.status === filterEstado);
    if (filterProducto !== 'all') result = result.filter(b => b.producto === filterProducto);
    if (filterFecha) result = result.filter(b => b.fecha === filterFecha);
    // Auto-sort: activo → completado → anulado, luego fecha desc
    const statusOrder = { activo: 0, completado: 1, anulado: 2 };
    result.sort((a, b) => {
      const sd = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
      if (sd !== 0) return sd;
      return (b.fecha || '').localeCompare(a.fecha || '');
    });
    return result;
  }, [businesses, filterText, filterEstado, filterProducto, filterFecha, clients, importadoras]);

  const uniqueProductos = useMemo(() => [...new Set(businesses.map(b => b.producto).filter(Boolean))].sort(), [businesses]);

  // Muestra alias si existe, sino razón social
  const getClientName = (id) => {
    const client = clients.find(c => c.id === id);
    return client ? (client.alias || client.razon_social) : 'N/A';
  };
  const getImportadoraName = (id) => { const i = importadoras.find(x => x.id === id); return i ? (i.alias || i.razon_social) : 'N/A'; };

  // Formato para selector: muestra alias y razón social
  const getClientDisplay = (client) => {
    if (client.alias) return `${client.alias} - ${client.razon_social}`;
    return client.razon_social;
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  // Auto-fill from selected client
  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      contacto: client?.contacto_nombre || '',
      email_contacto: client?.email || '',
      direccion_comercial: client?.direccion ? `${client.direccion}, ${client.comuna || ''}`.trim().replace(/,\s*$/, '') : ''
    }));
  };

  // Auto-fill cuentas and business_type when importadora changes
  const handleImportadoraChange = (impId) => {
    const imp = importadoras.find(i => i.id === impId);
    const alias = (imp?.alias || '').toUpperCase();
    setFormData(prev => ({
      ...prev,
      importadora_id: impId,
      business_type: alias,  // AGROTRANS o GLOBALGRAIN
      cuenta_corriente: ''
    }));
  };

  // Get cuenta options for selected importadora
  const getCuentaOptions = () => {
    const imp = importadoras.find(i => i.id === formData.importadora_id);
    const alias = (imp?.alias || '').toUpperCase();
    const cuentas = CUENTAS_POR_IMPORTADORA[alias];
    if (!cuentas) return [];
    return [
      { value: `CLP ${cuentas.clp}`, label: `CLP  ${cuentas.clp}` },
      { value: `USD ${cuentas.usd}`, label: `USD  ${cuentas.usd}` },
    ];
  };

  const handleProductoChange = (val) => {
    setFormData(prev => ({
      ...prev,
      producto: val,
      producto_otro: val === 'OTROS' ? prev.producto_otro : '',
      calidad: CALIDAD_POR_PRODUCTO[val] || prev.calidad
    }));
  };

  const getPayloadProducto = () => {
    return formData.producto === 'OTROS' && formData.producto_otro
      ? formData.producto_otro.toUpperCase()
      : formData.producto;
  };

  // Helpers para cronograma estructurado
  const addCronogramaItem = () => {
    setFormData(prev => ({
      ...prev,
      cronograma_items: [...prev.cronograma_items, { camiones: '', fecha: '' }]
    }));
  };

  const removeCronogramaItem = (index) => {
    setFormData(prev => ({
      ...prev,
      cronograma_items: prev.cronograma_items.filter((_, i) => i !== index)
    }));
  };

  const updateCronogramaItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cronograma_items: prev.cronograma_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Calcula total de camiones en cronograma
  const totalCamionesCronograma = formData.cronograma_items.reduce(
    (sum, item) => sum + (parseInt(item.camiones) || 0), 0
  );
  const cantidadCamionesNegocio = parseInt(formData.cantidad_camiones) || 0;
  const camionesFaltantes = cantidadCamionesNegocio - totalCamionesCronograma;
  const cronogramaCompleto = cantidadCamionesNegocio > 0 && camionesFaltantes === 0;

  // Convierte cronograma estructurado a texto para el backend
  const getCronogramaTexto = () => {
    const items = formData.cronograma_items.filter(item => item.camiones && item.fecha);
    if (items.length === 0) return '';
    return items.map(item => {
      const fechaFormatted = item.fecha.split('-').reverse().join('/');
      return `${item.camiones} CAMIONES EL ${fechaFormatted}`;
    }).join(' Y ');
  };

  // Convierte texto de cronograma a estructura (para edición)
  const parseCronogramaTexto = (texto) => {
    if (!texto) return [{ camiones: '', fecha: '' }];
    // Formato: "9 CAMIONES EL 12/02/2024 Y 9 CAMIONES EL 19/02/2024"
    const regex = /(\d+)\s*CAMIONES?\s*EL\s*(\d{2})\/(\d{2})(?:\/(\d{4}|\d{2}))?/gi;
    const items = [];
    let match;
    while ((match = regex.exec(texto)) !== null) {
      const camiones = match[1];
      const dia = match[2];
      const mes = match[3];
      let anio = match[4] || new Date().getFullYear().toString();
      if (anio.length === 2) anio = '20' + anio;
      items.push({ camiones, fecha: `${anio}-${mes}-${dia}` });
    }
    return items.length > 0 ? items : [{ camiones: '', fecha: '' }];
  };

  // Genera texto de condición de pago
  const getCondicionPagoTexto = () => {
    if (!formData.dias_pago) return '';
    return `${formData.dias_pago} DÍAS SEGÚN USD OBS DIA DESCARGA`;
  };

  // Extrae días de condición de pago texto
  const parseDiasPago = (texto) => {
    if (!texto) return '';
    const match = texto.match(/^(\d+)/);
    return match ? match[1] : '';
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    // Validar que el cronograma esté completo
    if (!cronogramaCompleto) {
      toast.error(`El cronograma debe sumar ${cantidadCamionesNegocio} camiones (faltan ${camionesFaltantes})`);
      return;
    }
    try {
      const payload = {
        business_type: formData.business_type,
        importadora_id: formData.importadora_id,
        client_id: formData.client_id,
        contacto: formData.contacto,
        direccion_comercial: formData.direccion_comercial,
        producto: getPayloadProducto(),
        cantidad_toneladas: parseFloat(formData.cantidad_toneladas),
        cantidad_camiones: parseInt(formData.cantidad_camiones),
        precio_unitario: parseFloat(formData.precio_unitario),
        calidad: formData.calidad,
        cronograma: getCronogramaTexto(),
        direccion_entrega: formData.direccion_entrega,
        condicion_pago: getCondicionPagoTexto(),
        cuenta_corriente: formData.cuenta_corriente,
        banco: formData.banco,
        emails_comprobantes: formData.emails_comprobantes,
        fecha: formData.fecha,
        cierre: formData.cierre
      };
      await axios.post(`${API}/businesses`, payload);
      toast.success('Negocio creado exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al crear negocio'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) { toast.error('La justificación es obligatoria'); return; }
    // Validar que el cronograma esté completo
    if (!cronogramaCompleto) {
      toast.error(`El cronograma debe sumar ${cantidadCamionesNegocio} camiones (faltan ${camionesFaltantes})`);
      return;
    }
    try {
      const payload = {
        importadora_id: formData.importadora_id,
        client_id: formData.client_id,
        business_type: formData.business_type,
        contacto: formData.contacto,
        emails_comprobantes: formData.email_contacto || formData.emails_comprobantes,
        direccion_comercial: formData.direccion_comercial,
        producto: getPayloadProducto(),
        cantidad_toneladas: parseFloat(formData.cantidad_toneladas),
        cantidad_camiones: parseInt(formData.cantidad_camiones),
        precio_unitario: parseFloat(formData.precio_unitario),
        calidad: formData.calidad,
        cronograma: getCronogramaTexto(),
        direccion_entrega: formData.direccion_entrega,
        condicion_pago: getCondicionPagoTexto(),
        cuenta_corriente: formData.cuenta_corriente,
        banco: formData.banco,
        fecha: formData.fecha,
        cierre: formData.cierre
      };
      await axios.put(`${API}/businesses/${selectedBusiness.id}`, { justification, data: payload });
      toast.success('Negocio actualizado');
      setIsEditModalOpen(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Error al actualizar'); }
  };

  const handleGeneratePDF = async (business) => {
    try {
      toast.info('Generando PDF...');
      const client = clients.find(c => c.id === business.client_id);
      const importadora = importadoras.find(i => i.id === business.importadora_id);
      await generateSalesOrderPDF(business, client, importadora);
      toast.success('PDF generado exitosamente');
    } catch (error) { toast.error('Error al generar PDF'); console.error(error); }
  };

  const resetForm = () => { setFormData({...INITIAL_FORM, cronograma_items: [{ camiones: '', fecha: '' }]}); setJustification(''); setSelectedBusiness(null); };

  const openEditModal = (biz) => {
    setSelectedBusiness(biz);
    const isStandard = ['MAIZ', 'TRIGO'].includes(biz.producto?.toUpperCase());
    setFormData({
      importadora_id: biz.importadora_id || '',
      client_id: biz.client_id || '',
      contacto: biz.contacto || '',
      email_contacto: biz.email_contacto || biz.emails_comprobantes || '',
      direccion_comercial: biz.direccion_comercial || '',
      producto: isStandard ? biz.producto.toUpperCase() : 'OTROS',
      producto_otro: isStandard ? '' : biz.producto || '',
      cantidad_toneladas: biz.cantidad_toneladas?.toString() || '',
      cantidad_camiones: biz.cantidad_camiones?.toString() || '',
      precio_unitario: biz.precio_unitario?.toString() || '',
      calidad: biz.calidad || '',
      cronograma_items: parseCronogramaTexto(biz.cronograma),
      direccion_entrega: biz.direccion_entrega || '',
      dias_pago: parseDiasPago(biz.condicion_pago),
      cuenta_corriente: biz.cuenta_corriente || '',
      banco: biz.banco || 'SANTANDER',
      emails_comprobantes: biz.emails_comprobantes || '',
      fecha: biz.fecha || '',
      cierre: biz.cierre || ''
    });
    setIsEditModalOpen(true);
  };

  const cuentaOptions = getCuentaOptions();
  const d = new Date();
  const todayFormatted = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;

  const renderForm = () => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto overflow-x-hidden pr-1">
      {/* Fila 1: N° Cierre + Fecha */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">N° Cierre *</Label>
          <Input value={formData.cierre} onChange={e => set('cierre', e.target.value.toUpperCase())} required className="uppercase" data-testid="biz-cierre-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Fecha *</Label>
          <Input type="date" value={formData.fecha} onChange={e => set('fecha', e.target.value)} required data-testid="biz-fecha-input" />
        </div>
      </div>

      {/* Fila 2: Importadora + Cliente */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Importadora *</Label>
          <Select value={formData.importadora_id || "none"} onValueChange={v => handleImportadoraChange(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="biz-importadora-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Seleccionar...</SelectItem>
              {importadoras.map(i => <SelectItem key={i.id} value={i.id}>{i.alias || i.razon_social}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Cliente *</Label>
          <Select value={formData.client_id || "none"} onValueChange={v => handleClientChange(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="biz-client-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Seleccionar...</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{getClientDisplay(c)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Fila 3: Contacto + Email (auto-filled from client) */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Contacto *</Label>
          <Input value={formData.contacto} onChange={e => set('contacto', e.target.value.toUpperCase())} required className="uppercase" data-testid="biz-contacto-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Email</Label>
          <Input value={formData.email_contacto} onChange={e => set('email_contacto', e.target.value)} data-testid="biz-email-input" />
        </div>
      </div>

      {/* Fila 4: Dirección Comercial (auto-filled from client) */}
      <div className="space-y-1.5">
        <Label className="text-xs">Dirección Comercial *</Label>
        <Input value={formData.direccion_comercial} onChange={e => set('direccion_comercial', e.target.value.toUpperCase())} required className="uppercase" data-testid="biz-dir-comercial-input" />
      </div>

      {/* Fila 5: Producto + Toneladas + Camiones */}
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Producto *</Label>
          <Select value={formData.producto || "none"} onValueChange={v => handleProductoChange(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="biz-producto-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Seleccionar...</SelectItem>
              <SelectItem value="MAIZ">MAIZ</SelectItem>
              <SelectItem value="TRIGO">TRIGO</SelectItem>
              <SelectItem value="OTROS">OTROS</SelectItem>
            </SelectContent>
          </Select>
          {formData.producto === 'OTROS' && (
            <Input value={formData.producto_otro} onChange={e => set('producto_otro', e.target.value.toUpperCase())} placeholder="Especifique..." className="uppercase mt-1.5" data-testid="biz-producto-otro-input" />
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Toneladas *</Label>
          <Input type="number" step="0.01" value={formData.cantidad_toneladas} onChange={e => set('cantidad_toneladas', e.target.value)} required data-testid="biz-toneladas-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Camiones *</Label>
          <Input type="number" value={formData.cantidad_camiones} onChange={e => set('cantidad_camiones', e.target.value)} required data-testid="biz-camiones-input" />
        </div>
      </div>

      {/* Fila 6: Precio + Calidad */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Precio USD/Ton *</Label>
          <Input type="number" step="0.01" value={formData.precio_unitario} onChange={e => set('precio_unitario', e.target.value)} required data-testid="biz-precio-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Calidad *</Label>
          <Input value={formData.calidad} onChange={e => set('calidad', e.target.value.toUpperCase())} required className="uppercase" data-testid="biz-calidad-input" />
        </div>
      </div>

      {/* Fila 7: Cronograma Estructurado */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Cronograma *</Label>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-medium ${cronogramaCompleto ? 'text-emerald-600' : camionesFaltantes > 0 ? 'text-amber-600' : 'text-red-600'}`}>
              {totalCamionesCronograma}/{cantidadCamionesNegocio} camiones
              {camionesFaltantes > 0 && ` (faltan ${camionesFaltantes})`}
              {camionesFaltantes < 0 && ` (${Math.abs(camionesFaltantes)} de más)`}
            </span>
          </div>
        </div>
        <div className="space-y-2 p-3 bg-slate-50 border border-slate-200 rounded-md">
          {formData.cronograma_items.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2">
                <Input 
                  type="number" 
                  min="1"
                  value={item.camiones} 
                  onChange={e => updateCronogramaItem(index, 'camiones', e.target.value)}
                  placeholder="Camiones"
                  className="w-24 text-center"
                  data-testid={`cronograma-camiones-${index}`}
                />
                <span className="text-xs text-slate-500">camiones el</span>
                <Input 
                  type="date"
                  value={item.fecha}
                  onChange={e => updateCronogramaItem(index, 'fecha', e.target.value)}
                  className="w-40"
                  data-testid={`cronograma-fecha-${index}`}
                />
              </div>
              {formData.cronograma_items.length > 1 && (
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => removeCronogramaItem(index)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                  data-testid={`cronograma-remove-${index}`}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          ))}
          {!cronogramaCompleto && camionesFaltantes > 0 && (
            <Button 
              type="button" 
              variant="outline" 
              size="sm" 
              onClick={addCronogramaItem}
              className="w-full border-dashed text-slate-600 hover:text-slate-900"
              data-testid="cronograma-add-btn"
            >
              <Plus size={14} className="mr-1" /> Agregar entrega
            </Button>
          )}
        </div>
      </div>

      {/* Fila 8: Dirección de Entrega */}
      <div className="space-y-1.5">
        <Label className="text-xs">Dirección de Entrega *</Label>
        <Input value={formData.direccion_entrega} onChange={e => set('direccion_entrega', e.target.value.toUpperCase())} required className="uppercase" data-testid="biz-dir-entrega-input" />
      </div>

      {/* Fila 9: Condición de Pago */}
      <div className="space-y-1.5">
        <Label className="text-xs">Condición de Pago *</Label>
        <div className="flex items-center gap-2">
          <Input 
            type="number" 
            min="0"
            value={formData.dias_pago} 
            onChange={e => set('dias_pago', e.target.value)} 
            required 
            className="w-20 text-center font-semibold"
            placeholder="0"
            data-testid="biz-dias-pago-input" 
          />
          <span className="text-sm text-slate-600 font-medium">DÍAS SEGÚN USD OBS DIA DESCARGA</span>
        </div>
      </div>

      {/* Fila 10: Cuenta Corriente + Banco */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Cuenta Corriente *</Label>
          {cuentaOptions.length > 0 ? (
            <Select value={formData.cuenta_corriente || "none"} onValueChange={v => set('cuenta_corriente', v === "none" ? "" : v)}>
              <SelectTrigger data-testid="biz-cuenta-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Seleccionar...</SelectItem>
                {cuentaOptions.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input value={formData.cuenta_corriente} onChange={e => set('cuenta_corriente', e.target.value)} required placeholder="Seleccione una importadora primero" data-testid="biz-cuenta-input" />
          )}
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Banco *</Label>
          <Input value={formData.banco} onChange={e => set('banco', e.target.value.toUpperCase())} required className="uppercase" data-testid="biz-banco-input" />
        </div>
      </div>

      {/* Fila 11: Emails para Comprobantes */}
      <div className="space-y-1.5">
        <Label className="text-xs">Emails para Comprobantes</Label>
        <Input value={formData.emails_comprobantes} onChange={e => set('emails_comprobantes', e.target.value)} placeholder="email1@ejemplo.com, email2@ejemplo.com" data-testid="biz-emails-input" />
      </div>
    </div>
  );

  if (user?.role !== 'MASTER') {
    return (<Layout><div className="text-center py-10"><p className="text-slate-500">No tienes permiso para acceder a este módulo</p></div></Layout>);
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="businesses-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-heading font-bold text-slate-900">Negocios</h1>
              <span className="text-sm text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded">
                {todayFormatted}
              </span>
            </div>
            <p className="text-slate-600 mt-1">Gestión de órdenes de venta</p>
          </div>
          <Button onClick={() => { resetForm(); setIsCreateModalOpen(true); }} className="bg-primary hover:bg-primary-hover text-white">
            <Plus size={18} className="mr-2" /> Nuevo Negocio
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2 items-end">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Búsqueda libre</span>
            <div className="relative">
              <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="N°, cliente, cierre, fecha..." value={filterText} onChange={e => setFilterText(e.target.value)} className="pl-7 h-7 text-xs w-52" />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Estado</span>
            <Select value={filterEstado} onValueChange={setFilterEstado}>
              <SelectTrigger className="h-7 text-xs w-32"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="activo">En Proceso</SelectItem>
                <SelectItem value="completado">Completado</SelectItem>
                <SelectItem value="anulado">Anulado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Producto</span>
            <Select value={filterProducto} onValueChange={setFilterProducto}>
              <SelectTrigger className="h-7 text-xs w-28"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {uniqueProductos.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-slate-500 uppercase">Fecha</span>
            <Input type="date" value={filterFecha} onChange={e => setFilterFecha(e.target.value)} className="h-7 text-xs w-32" />
          </div>
          {(filterText || filterEstado !== 'all' || filterProducto !== 'all' || filterFecha) && (
            <Button variant="ghost" size="sm" onClick={() => { setFilterText(''); setFilterEstado('all'); setFilterProducto('all'); setFilterFecha(''); }} className="h-7 text-xs text-slate-500 self-end">
              <X size={12} className="mr-1" /> Limpiar
            </Button>
          )}
          <span className="text-xs text-slate-400 self-end ml-auto">{filteredBusinesses.length} de {businesses.length}</span>
        </div>

        {/* Tabla */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">N° Negocio</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Cierre</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Estado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Importadora</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Producto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tons Pact.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Tons Entregadas</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Saldo</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Precio</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredBusinesses.map(biz => {
                const isCompleted = biz.status === 'completado';
                const isAnulado = biz.status === 'anulado';
                const tonsEntregadas = biz.toneladas_entregadas || 0;
                const saldo = biz.saldo_pendiente_tons ?? (biz.cantidad_toneladas - tonsEntregadas);
                const pct = biz.cantidad_toneladas > 0 ? Math.min(100, (tonsEntregadas / biz.cantidad_toneladas) * 100) : 0;
                return (
                  <tr key={biz.id} className={`hover:bg-slate-50 ${(isCompleted || isAnulado) ? 'opacity-75' : ''}`}>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">{biz.business_number}</span></td>
                    <td className="px-4 py-3">
                      {biz.fecha
                        ? <span className="text-xs font-mono text-slate-600">{biz.fecha.split('-').reverse().join('/')}</span>
                        : <span className="text-slate-300 text-xs">—</span>
                      }
                    </td>
                    <td className="px-4 py-3"><span className="text-xs font-mono text-slate-600">{biz.cierre || <span className="text-slate-300">—</span>}</span></td>
                    <td className="px-4 py-3">
                      {biz.status === 'completado'
                        ? <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700"><CheckCircle2 size={11} />Completado</span>
                        : biz.status === 'anulado'
                        ? <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><Ban size={11} />Anulado</span>
                        : <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700"><AlertCircle size={11} />En Proceso</span>
                      }
                    </td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><Building2 size={14} className="text-slate-400" /><span className="text-sm">{getClientName(biz.client_id)}</span></div></td>
                    <td className="px-4 py-3 text-sm">{getImportadoraName(biz.importadora_id)}</td>
                    <td className="px-4 py-3 text-sm font-medium">{biz.producto}</td>
                    <td className="px-4 py-3 text-sm font-mono">{biz.cantidad_toneladas?.toLocaleString('es-CL', {minimumFractionDigits:2, maximumFractionDigits:2})} T</td>
                    <td className="px-4 py-3">
                      <div className="space-y-0.5">
                        <span className={`text-sm font-mono font-semibold ${tonsEntregadas >= biz.cantidad_toneladas ? 'text-emerald-600' : 'text-slate-700'}`}>{tonsEntregadas.toLocaleString('es-CL', {minimumFractionDigits:2, maximumFractionDigits:2})} T</span>
                        <div className="w-20 h-1.5 bg-slate-200 rounded-full"><div className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-400' : 'bg-sky-400'}`} style={{width:`${pct}%`}}></div></div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className={`text-sm font-mono font-semibold ${saldo <= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>{Math.max(0,saldo).toLocaleString('es-CL', {minimumFractionDigits:2, maximumFractionDigits:2})} T</span></td>
                    <td className="px-4 py-3 text-sm"><div className="flex items-center gap-1 font-mono"><DollarSign size={14} className="text-slate-400" />{biz.precio_unitario?.toFixed(2)}</div></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1 flex-wrap">
                        <Button onClick={() => setViewBusiness(biz)} variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900" title="Ver orden de compra" data-testid={`view-biz-${biz.id}`}><Eye size={16} /></Button>
                        <Button onClick={() => handleGeneratePDF(biz)} variant="ghost" size="sm" className="text-emerald-600" title="Generar PDF"><Download size={16} /></Button>
                        {biz.status === 'activo' && (
                          <Button onClick={() => openEditModal(biz)} variant="ghost" size="sm" className="text-blue-600" title="Editar"><Pencil size={16} /></Button>
                        )}
                        {biz.status === 'activo' && tonsEntregadas === 0 && (
                          <Button onClick={() => anularNegocio(biz)} variant="ghost" size="sm" className="text-red-500 hover:bg-red-50" title="Anular negocio" data-testid={`anular-biz-${biz.id}`}><Ban size={16} /></Button>
                        )}
                        {biz.status === 'activo' && (
                          <Button onClick={() => completarNegocio(biz)} variant="ghost" size="sm" className="text-emerald-700 hover:bg-emerald-50" title="Completar negocio" data-testid={`complete-biz-${biz.id}`}><CheckCircle2 size={16} /></Button>
                        )}
                        {(biz.status === 'completado' || biz.status === 'anulado') && (
                          <Button onClick={() => restaurarNegocio(biz)} variant="ghost" size="sm" className="text-amber-600 hover:bg-amber-50" title="Restaurar negocio" data-testid={`restore-biz-${biz.id}`}><RotateCcw size={16} /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredBusinesses.length === 0 && (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-slate-400">{loading ? 'Cargando...' : 'No hay negocios registrados'}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Create Modal */}
        <Dialog open={isCreateModalOpen} onOpenChange={open => { if (!open) resetForm(); setIsCreateModalOpen(open); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nuevo Negocio</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {renderForm()}
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="submit-create-biz">Crear Negocio</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={open => { if (!open) resetForm(); setIsEditModalOpen(open); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Editar Negocio - {selectedBusiness?.business_number}</DialogTitle></DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              {renderForm()}
              <div className="space-y-1.5 pt-4 border-t">
                <Label className="text-xs text-red-600">Justificación (obligatoria) *</Label>
                <Input value={justification} onChange={e => setJustification(e.target.value)} required placeholder="Motivo de la edición" className="uppercase" data-testid="biz-justification-input" />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white" data-testid="submit-edit-biz">Actualizar Negocio</Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Modal - Orden de Compra */}
        <Dialog open={!!viewBusiness} onOpenChange={open => { if (!open) setViewBusiness(null); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye size={18} className="text-slate-500" />
                Orden de Compra — {viewBusiness?.business_number}
              </DialogTitle>
            </DialogHeader>
            {viewBusiness && (() => {
              const biz = viewBusiness;
              const client = clients.find(c => c.id === biz.client_id);
              const Row = ({ label, value }) => (
                <div className="flex items-start gap-2 py-1.5 border-b border-slate-100 last:border-0">
                  <span className="text-xs text-slate-400 uppercase w-40 shrink-0 pt-0.5">{label}</span>
                  <span className="text-sm font-medium text-slate-800">{value || <span className="text-slate-300 font-normal italic">—</span>}</span>
                </div>
              );
              return (
                <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                  {/* Cabecera: Nº negocio + Cierre + Fecha */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
                      <p className="text-[10px] text-blue-400 uppercase">N° Negocio</p>
                      <p className="font-bold text-blue-700 font-mono text-sm">{biz.business_number}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-center">
                      <p className="text-[10px] text-slate-400 uppercase">N° Cierre</p>
                      <p className="font-bold text-slate-700 font-mono text-sm">{biz.cierre || '—'}</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-center">
                      <p className="text-[10px] text-slate-400 uppercase">Fecha</p>
                      <p className="font-bold text-slate-700 font-mono text-sm">{biz.fecha || '—'}</p>
                    </div>
                  </div>

                  {/* Datos del cliente */}
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Cliente</p>
                    <Row label="Razón Social" value={client?.razon_social} />
                    <Row label="RUT" value={client?.rut} />
                    <Row label="Contacto" value={biz.contacto} />
                    <Row label="Email" value={biz.email_contacto || biz.emails_comprobantes} />
                    <Row label="Dirección Comercial" value={biz.direccion_comercial} />
                  </div>

                  {/* Datos del negocio */}
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Condiciones de Venta</p>
                    <Row label="Importadora" value={getImportadoraName(biz.importadora_id)} />
                    <Row label="Producto" value={biz.producto} />
                    <Row label="Calidad" value={biz.calidad} />
                    <Row label="Cantidad" value={`${biz.cantidad_camiones} camiones (${biz.cantidad_toneladas} Ton)`} />
                    <Row label="Precio USD/Ton" value={biz.precio_unitario ? `USD ${biz.precio_unitario.toFixed(2)}` : null} />
                    <Row label="Cronograma" value={biz.cronograma} />
                    <Row label="Dirección de Entrega" value={biz.direccion_entrega} />
                    <Row label="Condición de Pago" value={biz.condicion_pago} />
                  </div>

                  {/* Datos bancarios */}
                  <div className="border border-slate-200 rounded-sm p-3">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase mb-2">Datos Bancarios</p>
                    <Row label="Banco" value={biz.banco} />
                    <Row label="Cuenta Corriente" value={biz.cuenta_corriente} />
                    <Row label="Emails Comprobantes" value={biz.emails_comprobantes} />
                  </div>
                </div>
              );
            })()}
            <div className="flex gap-2 pt-2 border-t">
              <Button onClick={() => { handleGeneratePDF(viewBusiness); }} variant="outline" size="sm" className="text-emerald-600 border-emerald-200">
                <Download size={14} className="mr-1.5" /> Generar PDF
              </Button>
              <Button onClick={() => { openEditModal(viewBusiness); setViewBusiness(null); }} variant="outline" size="sm" className="text-blue-600 border-blue-200">
                <Pencil size={14} className="mr-1.5" /> Editar
              </Button>
              <Button onClick={() => setViewBusiness(null)} variant="ghost" size="sm" className="ml-auto">Cerrar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Businesses;
